<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      transition="dialog-bottom-transition"
      max-width="600px"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-spacer></v-spacer>
        <div class="text-right">
          <v-btn
            color="primary"
            dark
            outlined
            v-bind="attrs"
            v-on="on"
          >
            <v-icon class="mr-1 ml-n2">mdi-plus</v-icon>
            {{ $t('route_plan_rule_ui.create_rule') }}
          </v-btn>
        </div>
      </template>
      <v-card>
        <v-card-text>
          <v-form id="route-plan-rule-form" @submit.prevent="onSubmit" autocomplete="off">
            <v-row>
              <v-col>
                <div class="d-flex align-center mt-4">
                  <p
                    class="my-auto mr-3"
                    :class="!localModel.action?'blue--text text--darken-2 font-weight-bold':'grey--text'"
                  > Excluded </p>
                  <v-switch
                    v-model="localModel.action"
                    hide-details
                    class="mt-n1"
                    color="#1976D2"
                    v-on:change="updateDescription"
                  ></v-switch>
                  <p
                    class="my-auto ml-2"
                    :class="localModel.action?'blue--text text--darken-2 font-weight-bold':'grey--text'"
                  >
                    Included
                  </p>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <v-select
                  v-model="op"
                  :label="$t('route_plan_rule_ui.op')"
                  :items="opOptions"
                  v-on:change="updateDescription"
                  :error-messages="opErrors"
                  return-object />
              </v-col>
            </v-row>
            <v-text-field
              v-model="localModel.value"
              :label="$t('route_plan_rule_ui.value')"
              v-on:change="updateDescription"
              :error-messages="valueErrors" />
            <v-text-field
              v-model="localModel.note"
              :label="$t('route_plan_rule_ui.note')"/>
            <v-text-field
              v-model="localModel.description"
              :label="$t('route_plan_rule_ui.description')"
              :disabled="descriptionDisabled"
              :error-messages="descriptionErrors" />
            <v-row>
              <v-col class="text-right">
                <v-btn
                  type="submit"
                  color="primary"
                  :loading="isSaving"
                  outlined>
                  <v-icon>mdi-plus</v-icon>
                  {{ $t('route_plan_rule_ui.create_rule') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>
<script>
import CurrentUserMixin from '@/components/mixins/CurrentUserMixin'
import { required, numeric, helpers } from 'vuelidate/lib/validators'
import ValidationMixin from '@/components/mixins/ValidationMixin'
import ApiUtilitiesMixin from '@/components/mixins/ApiUtilitiesMixin'

const isNumber = helpers.withParams(
  { type: 'isNumber' },
  value => !isNaN(value)
)

const isRange = helpers.withParams(
  { type: 'isRange' },
  value => /^\d+:\d+$/.test(value)
)

const isValidRegex = helpers.withParams(
  { type: 'isValidRegex' },
  value => {
    try {
      new RegExp(value)
      return true
    } catch (e) {
      return false
    }
  }
)
export default {
  name: 'RoutePlanRuleCreate',
  components: {},
  mixins: [
    CurrentUserMixin,
    ValidationMixin,
    ApiUtilitiesMixin
  ],
  props: {
    customerId: {
      required: true
    }
  },
  validations: {
    op: {
      required,
    },
    localModel: {
      value: {
        required,
        isNumber: helpers.withParams(
          { type: 'isNumber' },
          function (value) {
            return this.op && (this.op.value === 'greaterthan' || this.op.value === 'lessthan') ? isNumber(value) : true
          }
        ),
        isRange: helpers.withParams(
          { type: 'isRange' },
          function (value) {
            return this.op && this.op.value === 'range' ? isRange(value) : true
          }
        ),
        isValidRegex: helpers.withParams(
          { type: 'isValidRegex' },
          function (value) {
            return this.op && this.op.value === 'regex' ? isValidRegex(value) : true
          }
        )
      },
      description: {
        required
      }
    }
  },
  data () {
    const defaultObj = {
      customer_id: this.customerId,
      action: 0,
      op: null,
      value: null,
      description: null
    }
    return {
      localModel: this.$orm('RoutePlanRule').createRecord(defaultObj),
      opOptions: [
        { label: '>=', text: '>=', value: 'greaterthan' },
        { label: '<=', text: '<=', value: 'lessthan' },
        { label: 'within the range', text: 'Range', value: 'range' },
        { label: 'that match', text: 'Matches', value: 'match' },
        { label: 'that don\'t match', text: 'Does Not Match', value: 'notmatch' },
        { label: 'starting with', text: 'Starts With', value: 'startwith' },
        { label: 'ending with', text: 'Ends With', value: 'endwith' },
        { label: 'that contain', text: 'Contains', value: 'contain' },
        { label: 'that don\'t contain', text: 'Does Not Contain', value: 'notcontain' },
        { text: 'Regex (Dev Only)', value: 'regex' }
      ],
      descriptionDisabled: true,
      formTouched: false,
      isSaving: false,
      op: null,
      dialog: false
    }
  },
  computed: {
    actionLabel () {
      return this.localModel.action === 0 ? this.$t('route_plan_rule_ui.exclude') : this.$t('route_plan_rule_ui.include')
    },
    opErrors () {
      const errors = []
      if (this.formTouched && !this.$v.op.required) { // Check if the form has been touched
        errors.push(this.$t('required'))
      }
      return errors
    },
    valueErrors () {
      const errors = []
      if (this.formTouched) { // Check if the form has been touched
        if (!this.$v.localModel.value.required) {
          errors.push(this.$t('required'))
        }
        if (this.op && (this.op.value === 'greaterthan' || this.op.value === 'lessthan') && !this.$v.localModel.value.isNumber) {
          errors.push(this.$t('route_plan_rule_ui.must_be_number'))
        }
        if (this.op && this.op.value === 'range' && !this.$v.localModel.value.isRange) {
          errors.push(this.$t('route_plan_rule_ui.must_be_range'))
        }
        if (this.op && this.op.value === 'regex' && !this.$v.localModel.value.isValidRegex) {
          errors.push(this.$t('route_plan_rule_ui.invalid_regex'))
        }
      }
      return errors
    },
    descriptionErrors () {
      return this.validationErrors([{
        prop: this.$v.localModel.description,
        message: this.$t('required')
      }])
    }
  },
  methods: {
    updateDescription () {
      if (this.op.value !== 'regex' && this.localModel.value !== null) {
        this.descriptionDisabled = true
        this.localModel.description = this.actionLabel + ' routes ' + this.op.label + ' ' + this.localModel.value
      } else if (this.op.value === 'regex') {
        this.descriptionDisabled = false
        this.localModel.description = ''
      }
    },
    onSubmit () {
      this.formTouched = true
      this.$v.$touch()
      if (this.$v.$invalid) return false
      this.isSaving = true
      this.localModel.op = this.op.value
      this.$orm('RoutePlanRule').createOrUpdate(this.localModel)
        .then(resp => {
          this.$emit('addRule')
          this.dialog = false
          this.formTouched = false
        })
        .catch((error) => {
          if (error) {
            this.renderRecordSaveErrorFlashBanner(error)
            throw (error)
          }
        })
        .finally(() => {
          this.isSaving = false
          this.localModel = this.setDefaultObj()
          this.op = null
        })
    },
    setDefaultObj () {
      return {
        customer_id: this.customerId,
        action: 0,
        op: null,
        value: null,
        description: null
      }
    }
  }
}
</script>

<style>
.v-input--switch:not(.v-input--switch--flat):not(.v-input--switch--inset) .v-input--switch__thumb {
  color: #1976D2;
}

.theme--light.v-input--switch .v-input--switch__track {
  color: #1976D2;
}

</style>
